import { toast as sonnerToast, Toaster as SonnerToaster } from 'sonner';
import styled from 'styled-components';

import type { ToasterProps, ToastProps } from './Toast.types';

const ToastWrapper = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  border: 1px solid transparent;
  background-color: #2a2e33;
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  padding: 12px 16px;
  font-family: 'Bw Nista Grotesk', sans-serif;
`;

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
`;

const ToastTitle = styled.p`
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: -0.3px;
  color: #ffffff;
  margin: 0;
`;

const ToastDescription = styled.p`
  font-size: 12px;
  line-height: 1.5;
  font-weight: 400;
  letter-spacing: -0.2px;
  color: #d4d4d4;
  margin: 0;
`;

const ToastActions = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  align-items: flex-end;
  justify-content: space-between;
  gap: 4px;
`;

const ActionButton = styled.button`
  font-family: 'Bw Nista Grotesk', sans-serif;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid transparent;
  font-size: 14px;
  line-height: 16px;
  font-weight: 500;
  letter-spacing: -0.3px;
  transition: background-color 200ms ease-in-out;
  white-space: nowrap;

  background-color: #f0f0f0;
  color: #2a2e33;
  border-radius: 8px;
  padding: 8px 12px;

  &:hover {
    background-color: #d4d4d4;
  }

  &:active {
    background-color: #d4d4d4;
  }

  &:focus-visible {
    outline: 2px solid #0798ed;
    outline-offset: 4px;
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #ffffff;
  transition: color 200ms ease-in-out;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
    border-radius: 100%;
  }

  &:focus-visible {
    outline: 2px solid #0798ed;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const CrossIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 32 32"
    fill="currentColor"
  >
    <path d="M25.53 24.47a.75.75 0 1 1-1.06 1.06L16 17.06l-8.47 8.47a.75.75 0 0 1-1.06-1.06L14.94 16 6.47 7.53a.75.75 0 0 1 1.06-1.06L16 14.94l8.47-8.47a.75.75 0 1 1 1.06 1.06L17.06 16l8.47 8.47Z" />
  </svg>
);

const Toast = ({ id, title, description, action, dismissible = true }: ToastProps) => {
  return (
    <ToastWrapper>
      <ToastContent>
        <ToastTitle>{title}</ToastTitle>
        {description && <ToastDescription>{description}</ToastDescription>}
      </ToastContent>

      <ToastActions>
        {dismissible && (
          <DismissButton onClick={() => sonnerToast.dismiss(id)} aria-label="Close">
            <CrossIcon />
          </DismissButton>
        )}
        {action && (
          <ActionButton
            onClick={() => {
              action.onClick();
              sonnerToast.dismiss(id);
            }}
          >
            {action.label}
          </ActionButton>
        )}
      </ToastActions>
    </ToastWrapper>
  );
};

export const Toaster = ({
  position = 'top-center',
  offset: customOffset = undefined
}: ToasterProps) => {
  const getDesktopOffset = () => {
    // Always use top: 64px in all cases for vertical position
    switch (position) {
      case 'top-left':
        return { top: '80px', left: '24px' };
      case 'top-center':
        return { top: '80px' };
      case 'top-right':
        return { top: '80px', right: '24px' };
      case 'bottom-left':
        return { bottom: '12px', left: '24px' };
      case 'bottom-center':
        return { bottom: '12px' };
      case 'bottom-right':
        return { bottom: '12px', right: '16px' };
    }
  };

  const desktopOffset = getDesktopOffset();

  const mobileOffset = () => {
    // Always use top: 64px in all cases for vertical position
    switch (position) {
      case 'top-left':
      case 'top-center':
      case 'top-right':
        return { top: '64px' };
      case 'bottom-left':
      case 'bottom-center':
      case 'bottom-right':
        return { bottom: '12px' };
    }
  };

  const offsetValue = { ...desktopOffset, ...(customOffset ?? {}) };

  return (
    <SonnerToaster
      position={position}
      mobileOffset={mobileOffset()}
      offset={offsetValue}
      visibleToasts={3}
    />
  );
};

export const showToast = (
  title: string,
  options?: Omit<ToastProps, 'id' | 'title'> & { duration?: number }
) => {
  const { duration, persistent, dismissible = true, ...rest } = options ?? {};
  return sonnerToast.custom(
    (id) => <Toast id={id} title={title} dismissible={dismissible} {...rest} />,
    { duration: persistent ? Infinity : duration, dismissible }
  );
};

showToast.dismiss = sonnerToast.dismiss;
